import { MessageCircle, Phone, Mail } from 'lucide-react';
import { PriceDisplay } from './PriceDisplay';
import { useLanguage } from '../config/language';

/**
 * PriceOrContact — renders either the price OR a "Contact for price" set of
 * buttons based on `item.priceOnRequest`.
 *
 * When `priceOnRequest` is true:
 *   - Hides the numeric price
 *   - Shows a Contact button that opens WhatsApp / Phone / Email using the
 *     store contact fields (whatsapp, phone, email).
 *
 * Props:
 *   - item: full item object (must have `price` and optional `store` or top-level
 *           `whatsapp/phone/email` for contact)
 *   - variant: 'card' (compact, default) | 'detail' (large, for detail pages)
 *   - itemLabel: short label used inside the WhatsApp message (e.g., "الرقم 050-...")
 */
export default function PriceOrContact({ item, variant = 'card', itemLabel = '' }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  if (!item) return null;

  const isRequest = Boolean(item.priceOnRequest);

  if (!isRequest) {
    return (
      <PriceDisplay
        priceAED={item.salePrice || item.price}
        originalPriceAED={item.salePrice ? item.price : null}
      />
    );
  }

  // Extract store contact info
  const store = item.store || {};
  const whatsapp = store.whatsapp || item.whatsapp || store.phone || item.phone;
  const phone = store.phone || item.phone;
  const email = store.email || item.email;

  const message = encodeURIComponent(
    isAr
      ? `مرحباً، أنا مهتم${itemLabel ? ` بـ${itemLabel}` : ''} وأود معرفة السعر`
      : `Hello, I'm interested${itemLabel ? ` in ${itemLabel}` : ''} and would like to know the price`
  );

  const btnBase = variant === 'detail'
    ? 'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all';

  const cleanPhone = (p) => (p || '').replace(/[^0-9]/g, '');

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0" data-testid="price-on-request">
      <span
        className={`inline-flex items-center py-1.5 rounded-full max-w-full min-w-0 ${variant === 'detail' ? 'gap-1.5 px-3 text-sm' : 'gap-1 px-2 text-xs'} font-semibold border whitespace-nowrap bg-slate-50 text-slate-700 border-slate-300 dark:bg-white/5 dark:text-white/90 dark:border-white/15`}
        data-testid="price-on-request-label"
      >
        <Phone className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{variant === 'card' ? (isAr ? 'تواصل للسعر' : 'Ask price') : (isAr ? 'التواصل لمعرفة السعر' : 'Contact for price')}</span>
      </span>

      {whatsapp && (
        <a
          href={`https://wa.me/${cleanPhone(whatsapp)}?text=${message}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} bg-emerald-500 text-white hover:bg-emerald-600`}
          data-testid="contact-whatsapp-btn"
        >
          <MessageCircle className="h-4 w-4" />
          <span>{isAr ? 'واتساب' : 'WhatsApp'}</span>
        </a>
      )}
      {variant === 'detail' && phone && (
        <a
          href={`tel:${cleanPhone(phone)}`}
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} bg-[#1E9BD9] text-white hover:bg-[#0284C7]`}
          data-testid="contact-phone-btn"
        >
          <Phone className="h-4 w-4" />
          <span>{isAr ? 'اتصال' : 'Call'}</span>
        </a>
      )}
      {variant === 'detail' && email && (
        <a
          href={`mailto:${email}`}
          onClick={(e) => e.stopPropagation()}
          className={`${btnBase} bg-gray-700 text-white hover:bg-gray-800`}
          data-testid="contact-email-btn"
        >
          <Mail className="h-4 w-4" />
          <span>{isAr ? 'إيميل' : 'Email'}</span>
        </a>
      )}
    </div>
  );
}

/**
 * Helper for filter logic: excludes `priceOnRequest` items from price-based
 * min/max filters and sorts (they have no comparable price).
 */
export function isPriceOnRequest(item) {
  return Boolean(item?.priceOnRequest);
}
